import * as React from "react";
import { useMemo, useRef } from "react";
import { observer } from "mobx-react-lite";
import { useStores } from "stores";
import { Header } from "./components";
import { ActionModalConfig } from "stores/ActionModalsStore";
import { observable } from "mobx";
import { ModalView } from "./ModalView";
import { Button, Flex } from "antd";

export type TActionModalProps<T = any> = {
  config: ActionModalConfig;
  style?: any;
  actionData: TActionDataType<T>;
  ref: any;
  onClose: () => void;
  onValidate: {
    callback?: () => Promise<any>;
  };
};

export type TActionDataType<T = any> = {
  isValid: boolean;
  data?: T;
};

function isClassComponent(component: any) {
  return (!!component.prototype && !!component.prototype.isReactComponent) || typeof component !== "function";
}

export const ActionModal = observer<{
  config: ActionModalConfig;
  visible: boolean;
}>(({ config, visible }) => {
  const { actionModals } = useStores();
  const { render: Render, options, id } = config;

  const actionData = useMemo(
    () =>
      observable({
        isValid: config.options.noValidation,
        data: config.options.initData || {},
      }),
    [config.options.initData, config.options.noValidation]
  );

  const bodyRef = useRef<{ onValidate?: () => Promise<any> }>();

  const { width = "750px", position = "center" } = options;

  const onClose = () => actionModals.close(id);

  const isActionLoading = config.actionStatus === "fetching";

  const onApply = (data: any) => {
    config.actionStatus = "fetching";

    return (
      options.onApply &&
      options
        .onApply(data)
        .then((res) => {
          config.actionStatus = "success";

          return res;
        })
        .catch((err: any) => {
          config.actionStatus = "error";
          return Promise.reject(err);
        })
    );
  };

  const onValidate: { callback?: () => Promise<any> } = useMemo(() => ({}), []);

  const handleClickApply = () => {
    if (bodyRef && bodyRef.current && onValidate.callback) {
      onValidate
        .callback()
        .then(onApply)
        .catch((err: any) => {
          config.actionStatus = "error";
          actionModals.rejectError(config.id, err);
        });
    } else {
      onApply(actionData.data);
    }
  };

  return (
    <ModalView
      width={width}
      position={position}
      onClose={onClose}
      style={{ visibility: visible ? "visible" : "hidden" }}
      config={config}
      isOverlayClose={options.isOverlayClose !== undefined ? options.isOverlayClose : true}
    >
      {!options.hideHeader && (
        <Header title={options.title} centered={options.centered} onClose={config.options.onCloseHeader || onClose} />
      )}
      {!isClassComponent(Render) ? (
        Render({ actionData, onClose, config })
      ) : (
        <Render config={config} actionData={actionData} onValidate={onValidate} onClose={onClose} ref={bodyRef} />
      )}
      {options.applyText || options.closeText ? (
        <Flex dir="row">
          {options.applyText &&
            React.isValidElement(options.applyText) &&
            React.cloneElement(options.applyText, {
              onClick: handleClickApply,
              disabled: isActionLoading || !actionData.isValid,
            } as any)}
          {options.applyText &&
            !React.isValidElement(options.applyText) &&
            (typeof options.applyText === "function" ? (
              React.createElement(
                options.applyText as React.FC<{
                  onClick: () => void;
                  disabled: boolean;
                }>,
                {
                  onClick: handleClickApply,
                  disabled: isActionLoading || !actionData.isValid,
                }
              )
            ) : (
              <Button
                //size="auto"
                onClick={handleClickApply}
                disabled={isActionLoading || !actionData.isValid}
              >
                {options.applyText}
              </Button>
            ))}
          {options.closeText &&
            (typeof options.closeText === "function" ? (
              React.createElement(
                options.closeText as React.FC<{
                  onClick: () => void;
                  disabled: boolean;
                }>,
                { onClick: onClose, disabled: isActionLoading }
              )
            ) : (
              <Button
                // size="auto"
                onClick={onClose}
                disabled={isActionLoading}
              >
                {options.closeText}
              </Button>
            ))}
        </Flex>
      ) : null}
    </ModalView>
  );
});

export const ActionModals: React.FC = observer(() => {
  const { actionModals } = useStores();

  return (
    <>
      {actionModals.pool.map((config, idx) => (
        <ActionModal
          key={config.id}
          config={config}
          visible={config.options.showOther || actionModals.pool.length - 1 === idx}
        />
      ))}
    </>
  );
});
